/**
 * SetupExternalCpoConnectionUseCase
 *
 * Use case for setting up an external CPO connection for an operator.
 * External CPO operators manage their own OCPI-compliant infrastructure
 * and connect to Nayax via the external CPO endpoint.
 *
 * This is a one-time operation during onboarding - operators cannot switch
 * between internal and external CPO types after initial setup.
 */

import type { OcpiAdminRepository } from '../data/ocpi-admin-repository';
import type { ExternalCpoConnectionRequestDto, OcpiVersion } from '../types/api-types';

const VALID_OCPI_VERSIONS: OcpiVersion[] = ['2.0', '2.1', '2.1.1', '2.2', '2.2.1'];

export class SetupExternalCpoConnectionUseCase {
  constructor(private readonly repository: OcpiAdminRepository) {}

  /**
   * Execute setup external CPO connection operation
   *
   * @param operatorId - Nayax operator ID
   * @param data - External CPO connection configuration
   * @throws Error if validation fails or API call fails
   */
  async execute(
    operatorId: number,
    data: ExternalCpoConnectionRequestDto
  ): Promise<void> {
    // Validate inputs
    this.validateInput(data);

    // Setup the external CPO connection
    await this.repository.setupExternalCpoConnection(operatorId, data);
  }

  /**
   * Validate the external CPO connection request data
   */
  private validateInput(data: ExternalCpoConnectionRequestDto): void {
    if (!data.csms_name?.trim()) {
      throw new Error('CSMS is required');
    }

    // Validate connection version
    if (!data.connection_version) {
      throw new Error('OCPI version is required');
    }

    if (!VALID_OCPI_VERSIONS.includes(data.connection_version)) {
      throw new Error(
        `Invalid OCPI version. Must be one of: ${VALID_OCPI_VERSIONS.join(', ')}`
      );
    }

    // Validate versions_url if provided
    if (data.versions_url) {
      try {
        new URL(data.versions_url);
      } catch {
        throw new Error('Invalid versions URL format. Must be a valid URL.');
      }
    }

    // token_a is optional but if provided should not be empty
    if (data.token_a !== undefined && data.token_a.trim() === '') {
      throw new Error('Token A cannot be empty if provided');
    }
  }
}
